import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import RefreshIcon from "@mui/icons-material/Refresh";
import { formatDistanceToNow } from "date-fns";
import { COLORS } from "../../assets/theme";
import FeatureChips from "../../components/superadmin/FeatureChips";
import {
  getFeatureKeys,
  listOrganizations,
  OrganizationRow,
  OrganizationsPage,
} from "../../services/superadminService";

const PAGE_SIZES = [10, 25, 50, 100];

const relativeOrNever = (value: string | null) => {
  if (!value) return "Never";
  try {
    return formatDistanceToNow(new Date(value), { addSuffix: true });
  } catch {
    return "—";
  }
};

/** A dormant account: never booked, or nothing in the last 30 days. */
const isDormant = (org: OrganizationRow) => {
  if (!org.usage?.lastBookingAt) return true;
  const last = new Date(org.usage.lastBookingAt).getTime();
  return Date.now() - last > 30 * 24 * 60 * 60 * 1000;
};

const Organizations: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [hideDeleted, setHideDeleted] = useState(true);
  const [data, setData] = useState<OrganizationsPage | null>(null);
  const [featureKeys, setFeatureKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Read the canonical key list once so a fourth feature needs no release.
    getFeatureKeys()
      .then(setFeatureKeys)
      .catch(() => setFeatureKeys([]));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listOrganizations({
        page,
        pageSize,
        search: debouncedSearch || undefined,
      });
      setData(result);
    } catch {
      // The interceptor already surfaced the message.
    }
    setLoading(false);
  }, [page, pageSize, debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = (data?.organizations || []).filter(
    (org) => !hideDeleted || !org.isDeleted
  );
  const windowDays = data?.windowDays ?? 30;
  const totalPages = data?.totalPages ?? 1;

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "1.5rem" }}>
          Organizations
        </Typography>
        <Typography sx={{ color: COLORS.placeHolderColor, fontSize: "0.9rem" }}>
          {data ? `${data.total} total` : "Loading…"} · activity counted over the
          last {windowDays} days
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${COLORS.BorderColor}`,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 2,
            flexWrap: "wrap",
            borderBottom: `1px solid ${COLORS.BorderColor}`,
          }}
        >
          <TextField
            size="small"
            placeholder="Search organizations"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={hideDeleted}
                onChange={(e) => setHideDeleted(e.target.checked)}
              />
            }
            label={
              <Typography sx={{ fontSize: "0.88rem" }}>Hide deleted</Typography>
            }
          />
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Refresh">
            <IconButton onClick={load} size="small">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <TableContainer>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "#fafafa" }}>
                <TableCell sx={{ fontWeight: 700 }}>Organization</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Activity
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Last booking</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Size
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={26} />
                  </TableCell>
                </TableRow>
              )}

              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Typography sx={{ color: COLORS.placeHolderColor }}>
                      No organizations match this search.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                rows.map((org) => (
                  <TableRow
                    key={org.id}
                    hover
                    onClick={() => navigate(`/superadmin/organizations/${org.id}`)}
                    sx={{
                      cursor: "pointer",
                      opacity: org.isDeleted ? 0.45 : 1,
                    }}
                  >
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.92rem" }}>
                        {org.name}
                        {org.isDeleted && (
                          <Typography
                            component="span"
                            sx={{
                              ml: 1,
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              color: COLORS.RED,
                            }}
                          >
                            DELETED
                          </Typography>
                        )}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.76rem",
                          color: COLORS.placeHolderColor,
                        }}
                      >
                        +{org.country} · {org.timezone}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                        {org.usage?.bookingsInWindow ?? 0}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.74rem",
                          color: COLORS.placeHolderColor,
                        }}
                      >
                        {org.usage?.bookingsTotal ?? 0} all time
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color: isDormant(org)
                            ? COLORS.ORANGE
                            : COLORS.BlackText,
                          fontWeight: isDormant(org) ? 600 : 400,
                        }}
                      >
                        {relativeOrNever(org.usage?.lastBookingAt ?? null)}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {org.usage?.clients ?? 0} clients
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.74rem",
                          color: COLORS.placeHolderColor,
                        }}
                      >
                        {org.usage?.staff ?? 0} staff ·{" "}
                        {org.usage?.businesses ?? 0} businesses
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          flexWrap: "wrap",
                        }}
                      >
                        <FeatureChips features={org.features} keys={featureKeys} />
                        {org.publicBrowseEnabled && (
                          <Tooltip title="Public catalogue open to visitors">
                            <Chip
                              size="small"
                              label="Public"
                              sx={{
                                fontSize: "0.72rem",
                                height: 22,
                                fontWeight: 600,
                                bgcolor: "#e6eef8",
                                color: "#2a5a8c",
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 2,
            p: 1.5,
            borderTop: `1px solid ${COLORS.BorderColor}`,
          }}
        >
          <Typography sx={{ fontSize: "0.82rem", color: COLORS.placeHolderColor }}>
            Rows per page
          </Typography>
          <Select
            size="small"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            sx={{ height: 32 }}
          >
            {PAGE_SIZES.map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
          <Typography sx={{ fontSize: "0.82rem" }}>
            Page {data?.page ?? page} of {totalPages}
          </Typography>
          <IconButton
            size="small"
            disabled={page <= 1 || loading}
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
          </IconButton>
          <IconButton
            size="small"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((prev) => prev + 1)}
          >
            <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Paper>
    </Box>
  );
};

export default Organizations;
